import { useCallback, useMemo } from 'react'
import { CategoryGroup } from '~/app/components/Cart/CartMisc/CartTabs'
import { CartItemsV3Query } from '~/__generated__/app/query/Cart/Cart.graphql'
import {
  isAvailableDomesticAccommodationOption,
  isAvailableDomesticBusOption,
  isAvailableDomesticLeisureOption,
  isAvailableDomesticTrainOption,
} from '~/app/components/Cart/CartOrderHelper'
import { filterChosenAndAvailableOptions } from '~/app/utils/TrainHelper'
import { flatten, map, pipe, reduce } from 'ramda'
import { CategoryType } from '~/__generated__/__types__'
import { toKRW } from '~/app/utils/krNumberToString'

export default function useCartItemHook(
  cartItems: CartItemsV3Query['cartItemsV3'],
  currentTab: CategoryGroup
) {
  const cloneItems = (cartItems: CartItemsV3Query['cartItemsV3']) => {
    return [...cartItems]
  }

  const filterOutNoOptionCartItems = (items: CartItemsV3Query['cartItemsV3']) => {
    return items.filter(item => item.options.length > 0)
  }

  const filterInCurrentTabItems = (items: CartItemsV3Query['cartItemsV3']) => {
    if (currentTab === CategoryGroup.ALL) {
      return items
    }
    return items.filter(item => {
      if (currentTab === CategoryGroup.ACCOMMODATION) {
        return item.type === CategoryType.DomesticReservation
      } else if (currentTab === CategoryGroup.TICKET) {
        return (
          item.type === CategoryType.DomesticLeisureGiftcard ||
          item.type === CategoryType.DomesticLeisureTicket
        )
      } else {
        return (
          currentTab === CategoryGroup.TRANSPORTATION &&
          (item.type === CategoryType.DomesticTrain ||
            item.type === CategoryType.DomesticBus ||
            item.type === CategoryType.DomesticLeisureRentacar)
        )
      }
    })
  }

  const currentTabItems = useMemo(() => {
    const newCartItems = filterOutNoOptionCartItems(cloneItems(cartItems))
    return filterInCurrentTabItems(newCartItems)
  }, [cartItems, currentTab])

  const getCountInfo = useCallback(cartItems => {
    const count = {
      checked: 0,
      availableChecked: 0,
      total: 0,
      availableTotal: 0,
      accommodation: 0,
      availableAccommodation: 0,
      availableCheckedAccommodation: 0,
      ticket: 0,
      availableTicket: 0,
      availableCheckedTicket: 0,
      rentACar: 0,
      availableRentACar: 0,
      availableCheckedRentACar: 0,
      train: 0,
      availableTrain: 0,
      availableCheckedTrain: 0,
      bus: 0,
      availableBus: 0,
      availableCheckedBus: 0,
    }
    for (const item of cartItems) {
      if (item.__typename === 'DomesticAccommodationCartItem') {
        count.accommodation += item.options.length
        item.options.forEach(option => {
          const isAvailable = isAvailableDomesticAccommodationOption(option)
          if (isAvailable) {
            count.availableTotal++
            count.availableAccommodation++
            if (option.isChosen) {
              count.availableChecked++
              count.availableCheckedAccommodation++
            }
          }
        })
      } else if (
        item.__typename === 'DomesticLeisureCartItem' &&
        item.type === CategoryType.DomesticLeisureRentacar
      ) {
        count.rentACar += item.options.length
        item.options.forEach(option => {
          const isAvailable = isAvailableDomesticLeisureOption(option)
          if (isAvailable) {
            count.availableTotal++
            count.availableRentACar++
            if (option.isChosen) {
              count.availableChecked++
              count.availableCheckedRentACar++
            }
          }
        })
      } else if (item.__typename === 'DomesticLeisureCartItem') {
        count.ticket += item.options.length
        item.options.forEach(option => {
          const isAvailable = isAvailableDomesticLeisureOption(option)
          if (isAvailable) {
            count.availableTotal++
            count.availableTicket++
            if (option.isChosen) {
              count.availableChecked++
              count.availableCheckedTicket++
            }
          }
        })
      } else if (item.__typename === 'DomesticTrainCartItem') {
        count.train += item.options.length
        item.options.forEach(option => {
          const isAvailable = isAvailableDomesticTrainOption(option)
          if (isAvailable) {
            count.availableTotal++
            count.availableTrain++
            if (option.isChosen) {
              count.availableChecked++
              count.availableCheckedTrain++
            }
          }
        })
      } else if (item.__typename === 'DomesticBusCartItem') {
        count.bus += item.options.length
        item.options.forEach(option => {
          const isAvailable = isAvailableDomesticBusOption(option)
          if (isAvailable) {
            count.availableTotal++
            count.availableBus++
            if (option.isChosen) {
              count.availableChecked++
              count.availableCheckedBus++
            }
          }
        })
      }
      for (const option of item.options) {
        count.total++
        if (option.isChosen) {
          count.checked++
        }
      }
    }
    return count
  }, [])

  const currentCount = useMemo(() => getCountInfo(currentTabItems), [currentTabItems])

  const count = useMemo(() => getCountInfo(cartItems), [cartItems])

  /**
   * 탭
   */
  const isVisibleTab = useMemo(() => {
    if (currentTab === CategoryGroup.ALL) {
      return true
    } else if (currentTab === CategoryGroup.ACCOMMODATION && count.accommodation > 0) {
      return true
    } else if (currentTab === CategoryGroup.TICKET && count.ticket > 0) {
      return true
    } else {
      return (
        currentTab === CategoryGroup.TRANSPORTATION &&
        (count.train > 0 || count.bus > 0 || count.rentACar > 0)
      )
    }
  }, [currentTab, count])

  const { totalPrice, totalMemberDiscountPrice } = useMemo(() => {
    return pipe(
      map<
        CartItemsV3Query['cartItemsV3'][number],
        CartItemsV3Query['cartItemsV3'][number]['options']
      >(item => item.options),
      flatten,
      filterChosenAndAvailableOptions,
      reduce(
        (acc, cur) => {
          acc.totalPrice +=
            cur.price * (cur.__typename === 'DomesticLeisureCartItemOption' ? cur.quantity : 1)

          if (cur.__typename === 'DomesticAccommodationCartItemOption' && !!cur.originalPrice) {
            acc.totalMemberDiscountPrice += cur.originalPrice - cur.price
          }
          return acc
        },
        {
          totalPrice: 0,
          totalMemberDiscountPrice: 0,
        }
      )
    )(cartItems)
  }, [cartItems])

  const isDisabled = useMemo(() => {
    if (count.availableChecked <= 0) {
      return true
    }
    /**
     * [FES-53] KTX 단독구매를 불가능 하도록 장바구니에서 막는다
     * [FES-150] KTX 단일상품 구매불가에 따른 안내 강화
     * 버튼을 disabled 상태에서 enabled 상태로 수정하고 예매하기 클릭시 메세지를 노출하도록 한다
     */
    return false
  }, [count])

  return {
    currentTabItems,
    currentCount,
    count,
    isVisibleTab,
    totalPrice,
    totalMemberDiscountPriceText: totalMemberDiscountPrice ? toKRW(totalMemberDiscountPrice) : '',
    isDisabled,
    filterInCurrentTabItems,
  }
}
